import React, { useState, useCallback, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { playbookApi } from '../api/playbookApi';
import type { AvailableTransition, ExecuteTransitionResponse } from '../types/playbook.types';

interface ForceTransitionModalProps {
  isOpen: boolean;
  instanceId: string;
  transition: AvailableTransition;
  onClose: () => void;
  onComplete: (result: ExecuteTransitionResponse) => void;
}

const ForceTransitionModal: React.FC<ForceTransitionModalProps> = ({
  isOpen,
  instanceId,
  transition,
  onClose,
  onComplete,
}) => {
  const [reason, setReason] = useState('');
  const [bypassGuards, setBypassGuards] = useState(true);
  const [bypassValidators, setBypassValidators] = useState(true);
  const [confirmed, setConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ExecuteTransitionResponse | null>(null);

  useEffect(() => {
    if (isOpen) {
      setReason('');
      setBypassGuards(true);
      setBypassValidators(true);
      setConfirmed(false);
      setLoading(false);
      setError(null);
      setResult(null);
    }
  }, [isOpen]);

  const handleForce = useCallback(async () => {
    if (!reason.trim() || !confirmed) return;
    setLoading(true);
    setError(null);
    try {
      const res = await playbookApi.forceTransition(instanceId, transition.transitionId, {
        bypassGuards,
        bypassValidators,
        reason: reason.trim(),
      });
      setResult(res);
      onComplete(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Force transition failed');
    } finally {
      setLoading(false);
    }
  }, [instanceId, transition.transitionId, reason, confirmed, bypassGuards, bypassValidators, onComplete]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-rose-200 dark:border-rose-800 bg-rose-50 dark:bg-rose-900/20 rounded-t-lg">
          <h3 className="text-lg font-semibold text-rose-800 dark:text-rose-200">
            Force Transition
          </h3>
          <button onClick={onClose} className="text-rose-400 hover:text-rose-600 dark:hover:text-rose-300">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-4">
          {result ? (
            <div className={`rounded-md border px-4 py-3 text-sm ${
              result.success
                ? 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-200'
                : 'border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-200'
            }`}>
              {result.success
                ? `Forced transition complete — now at '${result.newStatus?.name}'`
                : 'Force transition failed. Check diagnostics for details.'
              }
            </div>
          ) : (
            <>
              {/* Transition info */}
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Forcing transition: <strong className="text-gray-900 dark:text-white">{transition.name}</strong>
                {' '}to <strong className="text-gray-900 dark:text-white">{transition.toStatusName}</strong>
              </div>

              {/* Blocked reason */}
              {transition.blockedReason && (
                <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
                  <p className="font-medium mb-1">Currently blocked:</p>
                  <p>{transition.blockedReason}</p>
                </div>
              )}

              {/* Bypass options */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <input
                    type="checkbox"
                    checked={bypassGuards}
                    onChange={(e) => setBypassGuards(e.target.checked)}
                    className="rounded border-gray-300 text-rose-600 focus:ring-rose-500 dark:border-gray-600"
                  />
                  Bypass guard checks
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <input
                    type="checkbox"
                    checked={bypassValidators}
                    onChange={(e) => setBypassValidators(e.target.checked)}
                    className="rounded border-gray-300 text-rose-600 focus:ring-rose-500 dark:border-gray-600"
                  />
                  Bypass validator checks
                </label>
              </div>

              {/* Reason */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Reason for override <span className="text-rose-500">*</span>
                </label>
                <textarea
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-rose-500"
                  rows={3}
                  placeholder="Explain why this transition needs to be forced..."
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
              </div>

              {/* Confirmation */}
              <label className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                  className="mt-0.5 rounded border-gray-300 text-rose-600 focus:ring-rose-500 dark:border-gray-600"
                />
                <span>I understand this bypasses compliance checks and will be recorded in the audit trail.</span>
              </label>

              {error && (
                <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-200">
                  {error}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            {result ? 'Close' : 'Cancel'}
          </button>
          {!result && (
            <button
              type="button"
              onClick={handleForce}
              disabled={!reason.trim() || !confirmed || loading}
              className="rounded-md bg-rose-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading ? 'Forcing...' : 'Force Transition'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForceTransitionModal;
